import { useEffect, useState } from 'react'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { AlertTriangle, HelpCircle, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

/**
 * VISN-150 axis 2 — tenant-wide KG-freshness banner.
 *
 * Mirrors the GET /v1/knowledge-graph/freshness response shape. The
 * three rendered states map to the `in_sync` discriminant:
 *
 *  - `in_sync === true`  → render nothing (banner only fires when
 *                          the tenant is meaningfully behind).
 *  - `in_sync === false` → "Regen recommended" amber banner with
 *                          the missing-features list and a
 *                          "Reingest all stale" button that POSTs
 *                          to `/regen-stale`.
 *  - `in_sync === null`  → "Freshness unknown" muted banner with
 *                          the backend's `error_code` for ops
 *                          ("repo_missing", "kg_meta_missing",
 *                          "kg_meta_unparseable", "gitea_unreachable",
 *                          plus the FE-only "backend_unreachable").
 *                          The Reingest button is rendered ONLY for
 *                          the two codes a regen self-heals
 *                          (`kg_meta_missing`, `repo_missing` —
 *                          see `fixableUnknownCodes` below); the
 *                          remaining codes are left buttonless
 *                          because clicking would either fail
 *                          (`gitea_unreachable`, `backend_unreachable`)
 *                          or silently overwrite an out-of-band
 *                          edit (`kg_meta_unparseable`).
 *
 * The banner re-fetches whenever `refreshNonce` increments — the
 * parent page bumps it after a successful regen-stale POST so the
 * banner reflects the in-flight queue. The actual freshness signal
 * doesn't clear until the regen lands; that's intentional (don't lie
 * to the user before the worker has committed).
 */
interface KgFreshnessResponse {
  in_sync: boolean | null
  missing_features: string[]
  tenant_consumed_features: string[]
  current_consumed_features: string[]
  last_commit_sha: string | null
  last_commit_at: string | null
  /**
   * Discriminated source of an `in_sync: null` verdict. The first
   * four values come from the backend (`getKgFreshness` in
   * `knowledge-graph.service.ts`). The fifth — `backend_unreachable`
   * — is FE-side only: it fires when the FreshnessBanner's own
   * fetch rejects (auth failure, 5xx before the structured
   * `error_code` path, browser offline). Distinguishing it from
   * `gitea_unreachable` keeps ops dashboards honest — chasing
   * "Gitea is down" when the actual problem is the NestJS pod or
   * the network between the browser and the LB sends the operator
   * to the wrong service (greptile P2 on PR #12108).
   */
  error_code?:
    | 'repo_missing'
    | 'kg_meta_missing'
    | 'kg_meta_unparseable'
    | 'gitea_unreachable'
    | 'backend_unreachable'
}

interface FreshnessBannerProps {
  /** Bump from the parent to force a refetch (after a regen-stale POST). */
  refreshNonce: number
  /** Parent hook so the banner can drive the parent's own refresh. */
  onRegenStaleQueued: () => void
}

export function FreshnessBanner({
  refreshNonce,
  onRegenStaleQueued,
}: FreshnessBannerProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<KgFreshnessResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenInFlight, setRegenInFlight] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiService
      .fetchOrError(apiService.getUrl('v1/knowledge-graph/freshness'))
      .then((res) => res.json() as Promise<KgFreshnessResponse>)
      .then((body) => {
        if (!cancelled) setData(body)
      })
      .catch((err) => {
        // Fetch error: the FE couldn't even reach the backend's
        // /freshness endpoint (network drop, auth failure, NestJS 5xx
        // before the structured error_code path). Surface this as
        // `backend_unreachable` rather than `gitea_unreachable` —
        // the second is reserved for "BE reached, but Gitea
        // misbehaved", and conflating the two would send the
        // operator chasing Gitea when the actual fault is closer
        // to the browser (greptile P2 on PR #12108).
        console.error('[FreshnessBanner] freshness fetch failed:', err)
        if (!cancelled) {
          setData({
            in_sync: null,
            missing_features: [],
            tenant_consumed_features: [],
            current_consumed_features: [],
            last_commit_sha: null,
            last_commit_at: null,
            error_code: 'backend_unreachable',
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce])

  const onRegenAll = async () => {
    setRegenInFlight(true)
    try {
      const res = await apiService.fetchOrError(
        apiService.getUrl('v1/knowledge-graph/regen-stale'),
        { method: 'POST' },
      )
      const body: { enqueued_count: number } = await res.json()
      toast.success(
        t('common:knowledge_graph.regen_stale_success', {
          count: body.enqueued_count,
          defaultValue: `Queued ${body.enqueued_count} regens — banner clears when they complete.`,
        }),
      )
      onRegenStaleQueued()
    } catch (err) {
      console.error('[FreshnessBanner] regen-stale failed:', err)
      toast.error(
        t(
          'common:knowledge_graph.regen_stale_failed',
          'Regen-stale failed — see browser console for details.',
        ),
      )
    } finally {
      setRegenInFlight(false)
    }
  }

  if (loading || !data) return null
  // Restructured as explicit positive matches rather than a chain of
  // negative guards: only render when `in_sync` is one of the two
  // banner-firing values. An undefined / unexpected value (e.g. a
  // test that swaps the freshness response with a different shape)
  // falls through to "no banner" instead of crashing on a missing
  // `missing_features` array.
  if (data.in_sync !== false && data.in_sync !== null) return null

  if (data.in_sync === null) {
    // VISN-150 PR8: not every "unknown" state is unfixable.
    //   - kg_meta_missing — Gitea repo exists but the writer hasn't
    //     stamped packages/kg-meta.ts there yet. A regen fixes it
    //     immediately because PR3's writer fires unconditionally
    //     on every regen.
    //   - repo_missing — tenant has never been regenerated. The
    //     kg-regen worker auto-provisions the repo on the first
    //     regen call (see kg-regen.worker.ts), so a regen also
    //     fixes this.
    //   - gitea_unreachable — Gitea itself is down or returning
    //     5xx. Clicking Reingest would just enqueue jobs that
    //     fail in the worker; the operator needs to fix Gitea
    //     before regen-stale is useful.
    //   - kg_meta_unparseable — the file is present but malformed.
    //     Likely an out-of-band edit; a regen would overwrite it,
    //     but operator inspection first is the safer default. We
    //     could promote this to "fixable" later if ops feedback
    //     says the always-overwrite story is fine.
    const fixableUnknownCodes: ReadonlyArray<KgFreshnessResponse['error_code']> = [
      'kg_meta_missing',
      'repo_missing',
    ]
    const isFixable = fixableUnknownCodes.includes(data.error_code)
    return (
      <div
        data-testid="kg-freshness-unknown"
        className="flex items-start gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300"
      >
        <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {t('common:knowledge_graph.freshness_unknown_title', 'Freshness unknown')}
          </p>
          <p className="mt-0.5 text-xs">
            {t('common:knowledge_graph.freshness_unknown_detail', {
              code: data.error_code ?? 'unknown',
              defaultValue: `Couldn't read this tenant's kg-meta.ts (${data.error_code ?? 'unknown'}). Run a regen if this tenant has never been processed.`,
            })}
          </p>
        </div>
        {isFixable && (
          <button
            type="button"
            onClick={onRegenAll}
            disabled={regenInFlight}
            data-testid="kg-regen-stale-button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-400 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-dark-card dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw className={regenInFlight ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            {regenInFlight
              ? t('common:knowledge_graph.regen_stale_queuing', 'Queuing…')
              : t('common:knowledge_graph.regen_stale_button', 'Reingest all stale')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      data-testid="kg-freshness-stale"
      className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">
          {t('common:knowledge_graph.freshness_stale_title', 'Regen recommended')}
        </p>
        <p className="mt-0.5 text-xs" data-testid="kg-freshness-missing">
          {t('common:knowledge_graph.freshness_stale_detail', {
            features: (data.missing_features ?? []).join(', '),
            defaultValue: `New parser-feature support landed since this tenant's last regen — missing: ${(data.missing_features ?? []).join(', ')}.`,
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={onRegenAll}
        disabled={regenInFlight}
        data-testid="kg-regen-stale-button"
        className="inline-flex items-center gap-2 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-700 dark:bg-dark-card dark:text-amber-200 dark:hover:bg-amber-900/30"
      >
        <RefreshCw className={regenInFlight ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
        {regenInFlight
          ? t('common:knowledge_graph.regen_stale_queuing', 'Queuing…')
          : t('common:knowledge_graph.regen_stale_button', 'Reingest all stale')}
      </button>
    </div>
  )
}
